/**
 * Página "Notificaciones" para jugadores
 * 
 * Muestra:
 * - Notificaciones recientes
 * - Alertas importantes
 * - Mensajes del sistema
 * - Configuración de notificaciones
 */

import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import API from '../../services/api';
import { debug, error } from '../../utils/logger';
import ShareSocialButton from '../../components/common/ShareSocialButton';

const PlayerNotificaciones = () => {
  const { playerData: _playerData } = useOutletContext(); // Prefijo con _ para evitar warning
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalNoLeidas: 0, totalLeidas: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  // Cargar notificaciones desde MongoDB
  const loadNotifications = async (page = 1, type = 'all', unreadOnly = false) => {
    try {
      setLoading(true);
      debug('Cargando notificaciones del futbolista', { page, type, unreadOnly });

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });

      if (type !== 'all') {
        params.append('type', type);
      }

      if (unreadOnly) {
        params.append('unreadOnly', 'true');
      }

      const response = await API.get(`/player/notificaciones-mongo?${params}`);

      if (response.data.success) {
        setNotifications(response.data.data.notificaciones);
        setStats(response.data.data.estadisticas);
        setCurrentPage(response.data.data.paginacion.paginaActual);
        setTotalPages(response.data.data.paginacion.totalPaginas);

        debug('Notificaciones cargadas exitosamente', {
          total: response.data.data.notificaciones.length,
          noLeidas: response.data.data.estadisticas.totalNoLeidas
        });
      }
    } catch (err) {
      error('Error al cargar notificaciones', err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    loadNotifications(1, filterType, showUnreadOnly);
  }, [filterType, showUnreadOnly]);

  const markAsRead = async (notificationId) => {
    try {
      debug('🔄 Marcando notificación como leída', { notificationId });

      // Actualizar el estado local INMEDIATAMENTE para mejor UX
      const updatedNotifications = notifications.map(notif =>
        notif.id === notificationId
          ? { ...notif, leida: true, fechaLectura: new Date().toISOString() }
          : notif
      );

      const updatedStats = {
        totalNoLeidas: Math.max(0, stats.totalNoLeidas - 1),
        totalLeidas: stats.totalLeidas + 1
      };

      // Actualizar estado local inmediatamente
      setNotifications(updatedNotifications);
      setStats(updatedStats);

      debug('✅ Estado local actualizado inmediatamente');

      // Luego hacer la petición al backend
      const response = await API.put(`/player/notificaciones-mongo/${notificationId}/read`);

      if (response.data.success) {
        debug('✅ Notificación marcada como leída en el servidor');
      } else {
        debug('⚠️ Respuesta del servidor no exitosa, pero estado local ya actualizado');
      }
    } catch (err) {
      error('❌ Error al marcar notificación como leída', err);

      // En caso de error, mantener el estado local actualizado
      // porque la función suele funcionar en el backend
      debug('🔄 Manteniendo cambio local a pesar del error');
    }
  };

  const containerStyle = {
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: window.innerWidth <= 768 ? '20px' : '30px',
    marginBottom: '25px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #E8ECFF'
  };

  const titleStyle = {
    fontSize: window.innerWidth <= 768 ? '1.8rem' : '2.2rem',
    color: '#061156',
    fontWeight: '700',
    marginBottom: '30px',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '15px'
  };

  const sectionTitleStyle = {
    fontSize: window.innerWidth <= 768 ? '1.4rem' : '1.6rem',
    color: '#061156',
    fontWeight: '700',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  const getNotificationIcon = (tipo, icono) => {
    // Si la notificación tiene un icono específico, usarlo
    if (icono) {
      return icono === 'person_alert' ? '🤝' :
             icono === 'check_circle' ? '✅' :
             icono === 'notifications' ? '🔔' :
             icono === 'sports_soccer' ? '⚽' :
             icono === 'emoji_events' ? '🏆' :
             '📢';
    }

    // Fallback basado en el tipo
    switch (tipo) {
      case 'registration': return '🤝';
      case 'achievement': return '🏆';
      case 'match': return '⚽';
      case 'system': return '✅';
      case 'payment': return '💳';
      case 'announcement': return '📢';
      case 'reminder': return '⏰';
      default: return '🔔';
    }
  };

  const getNotificationColor = (tipo, color) => {
    // Si la notificación tiene un color específico, usarlo
    if (color) {
      return color;
    }

    // Fallback basado en el tipo
    switch (tipo) {
      case 'registration': return '#FF9800'; // Naranja para inscripciones
      case 'achievement': return '#4CAF50'; // Verde para logros
      case 'match': return '#2196F3'; // Azul para partidos
      case 'system': return '#4CAF50'; // Verde para sistema
      case 'payment': return '#9C27B0'; // Morado para pagos
      case 'announcement': return '#FF5722'; // Rojo-naranja para anuncios
      case 'reminder': return '#FFC107'; // Amarillo para recordatorios
      default: return '#3498db';
    }
  };

  const getNotificationTypeLabel = (tipo) => {
    switch (tipo) {
      case 'registration': return 'Inscripciones';
      case 'achievement': return 'Logros';
      case 'match': return 'Partidos';
      case 'system': return 'Sistema';
      case 'payment': return 'Pagos';
      case 'announcement': return 'Anuncios';
      case 'reminder': return 'Recordatorios';
      default: return 'General';
    }
  };

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>
        🔔 Notificaciones
      </h1>

      {/* Resumen de Notificaciones */}
      <div style={cardStyle}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr 1fr' : 'repeat(3, 1fr)',
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div style={{
            backgroundColor: '#E3F2FD',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px solid #2196F3'
          }}>
            <div style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: '#1565C0',
              marginBottom: '5px'
            }}>
              {stats.totalNoLeidas + stats.totalLeidas}
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: '#1565C0',
              fontWeight: '500'
            }}>
              Total
            </div>
          </div>

          <div style={{
            backgroundColor: '#FFF3E0',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px solid #FF9800'
          }}>
            <div style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: '#E65100',
              marginBottom: '5px'
            }}>
              {stats.totalNoLeidas}
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: '#E65100',
              fontWeight: '500'
            }}>
              Sin leer
            </div>
          </div>
          
          <div style={{
            backgroundColor: '#E8F5E8',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px solid #4CAF50'
          }}>
            <div style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: '#2E7D32',
              marginBottom: '5px'
            }}>
              {notifications.filter(n => n.leida).length}
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: '#2E7D32',
              fontWeight: '500'
            }}>
              Leídas
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          🔍 Filtros
        </h2>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '15px',
          alignItems: 'center'
        }}>
          <div>
            <label style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#061156',
              marginBottom: '5px',
              display: 'block'
            }}>
              Tipo:
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '2px solid #E8ECFF',
                fontSize: '0.9rem',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="all">Todas</option>
              <option value="registration">Inscripciones</option>
              <option value="system">Sistema</option>
              <option value="achievement">Logros</option>
              <option value="match">Partidos</option>
              <option value="payment">Pagos</option>
            </select>
          </div>

          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#061156'
            }}>
              <input
                type="checkbox"
                checked={showUnreadOnly}
                onChange={(e) => setShowUnreadOnly(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Solo no leídas
            </label>
          </div>
        </div>
      </div>

      {/* Lista de Notificaciones */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          📋 Mis Notificaciones
        </h2>

        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⏳</div>
            Cargando notificaciones...
          </div>
        ) : notifications.length > 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            {notifications.map((notif) => (
              <div 
                key={notif.id} 
                style={{
                  backgroundColor: notif.leida ? '#F5F5F5' : '#FFF3E0',
                  padding: '20px',
                  borderRadius: '12px',
                  border: notif.leida ? '2px solid #E0E0E0' : '2px solid #FFB74D',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => !notif.leida && markAsRead(notif.id)}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '10px',
                  flexWrap: 'wrap',
                  gap: '10px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <span style={{
                      fontSize: '1.5rem',
                      backgroundColor: getNotificationColor(notif.tipo, notif.color),
                      color: 'white',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {getNotificationIcon(notif.tipo, notif.icono)}
                    </span>
                    <div>
                      <h4 style={{
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        color: '#061156',
                        margin: '0 0 5px 0'
                      }}>
                        {notif.titulo}
                      </h4>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px',
                        flexWrap: 'wrap'
                      }}>
                        <p style={{
                          fontSize: '0.85rem',
                          color: '#666',
                          margin: 0
                        }}>
                          📅 {notif.tiempoTranscurrido}
                        </p>
                        <span style={{
                          fontSize: '0.75rem',
                          backgroundColor: getNotificationColor(notif.tipo, notif.color),
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontWeight: '500'
                        }}>
                          {getNotificationTypeLabel(notif.tipo)}
                        </span>
                        {notif.prioridad === 'high' && (
                          <span style={{
                            fontSize: '0.75rem',
                            backgroundColor: '#F44336',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontWeight: '500'
                          }}>
                            🔴 Alta prioridad
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {!notif.leida && (
                    <span style={{
                      backgroundColor: '#FF9800',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      fontWeight: '600'
                    }}>
                      Nueva
                    </span>
                  )}
                </div>
                
                <p style={{
                  fontSize: '1rem',
                  color: '#333',
                  margin: '0 0 0 50px',
                  lineHeight: '1.5'
                }}>
                  {notif.mensaje}
                </p>

                {/* Botones de acción */}
                <div style={{
                  marginTop: '15px',
                  marginLeft: '50px',
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}>
                  {!notif.leida && (
                    <button
                      style={{
                        backgroundColor: '#4352A9',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notif.id);
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#061156';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#4352A9';
                      }}
                    >
                      Marcar como leída
                    </button>
                  )}

                  {/* Botón de compartir para notificaciones de logros */}
                  {notif.tipo === 'achievement' && notif.metadata?.partidoId && notif.metadata?.futbolistaId && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <ShareSocialButton
                        title={`🏆 ${notif.titulo}`}
                        text={`¡He conseguido nuevos logros en Liga ProKids! ${notif.mensaje}`}
                        url={`${window.location.origin}/share/partido/${notif.metadata.partidoId}/futbolista/${notif.metadata.futbolistaId}`}
                        hashtags={['LigaProKids', 'ProPoints', 'Futbol']}
                        onShare={(platform) => {
                          debug(`Compartido en ${platform}`, { notificationId: notif.id });
                          // Registrar el compartido
                          API.post('/postgres/share/track', {
                            partidoId: notif.metadata.partidoId,
                            futbolistaId: notif.metadata.futbolistaId,
                            platform
                          }).catch(err => error('Error tracking share:', err));
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            backgroundColor: '#F5F5F5',
            padding: '40px',
            borderRadius: '15px',
            textAlign: 'center',
            border: '2px dashed #CCCCCC'
          }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: '15px'
            }}>
              📭
            </div>
            <p style={{
              fontSize: '1.2rem',
              color: '#666',
              margin: 0,
              fontWeight: '500'
            }}>
              No tienes notificaciones
            </p>
          </div>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            marginTop: '30px',
            padding: '20px',
            backgroundColor: '#F8F9FF',
            borderRadius: '12px'
          }}>
            <button
              onClick={() => loadNotifications(currentPage - 1, filterType, showUnreadOnly)}
              disabled={currentPage === 1}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: currentPage === 1 ? '#E0E0E0' : '#4352A9',
                color: currentPage === 1 ? '#999' : 'white',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              ← Anterior
            </button>

            <span style={{
              fontSize: '0.9rem',
              color: '#666',
              fontWeight: '500'
            }}>
              Página {currentPage} de {totalPages}
            </span>

            <button
              onClick={() => loadNotifications(currentPage + 1, filterType, showUnreadOnly)}
              disabled={currentPage === totalPages}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: currentPage === totalPages ? '#E0E0E0' : '#4352A9',
                color: currentPage === totalPages ? '#999' : 'white',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>

      {/* Configuración de Notificaciones */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          ⚙️ Configuración
        </h2>
        
        <div style={{
          backgroundColor: '#E3F2FD',
          padding: '20px',
          borderRadius: '12px',
          border: '2px solid #2196F3'
        }}>
          <h4 style={{
            fontSize: '1.1rem',
            fontWeight: '700',
            color: '#1565C0',
            margin: '0 0 15px 0'
          }}>
            📱 Tipos de Notificaciones
          </h4>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)',
            gap: '15px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span>🏆</span>
              <span style={{ color: '#1565C0', fontWeight: '500' }}>
                Logros y reconocimientos
              </span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span>⚽</span>
              <span style={{ color: '#1565C0', fontWeight: '500' }}>
                Jornadas y partidos
              </span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span>⭐</span>
              <span style={{ color: '#1565C0', fontWeight: '500' }}>
                ProPoints y ranking
              </span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span>⚙️</span>
              <span style={{ color: '#1565C0', fontWeight: '500' }}>
                Actualizaciones del sistema
              </span>
            </div>
          </div>
          
          <p style={{
            fontSize: '0.9rem',
            color: '#1565C0',
            margin: '15px 0 0 0',
            fontStyle: 'italic'
          }}>
            💡 Las notificaciones se envían automáticamente según tu actividad en la plataforma.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlayerNotificaciones;
